/**
 * Documenting and correcting an allergy (DECISIONS.md #46).
 *
 * Mirrors `api/src/Continuum.Api/Safety/SafetyEndpoints.cs`'s two allergy routes and the codes
 * `AllergyContent.Validate` and `AllergyWriteService` return.
 *
 * **Two routes, both POST.** A first version is a POST to the collection; a correction is a POST to
 * that row's `/versions`, which appends the next version and leaves the one on screen in the record
 * (AGENTS.md §3 rule 2). There is no PUT and no DELETE, because there is no resource to replace and
 * nothing to remove — an allergy documented by mistake becomes `entered-in-error`, which is a
 * correction like any other.
 *
 * **The two routes take the same body**, and the difference is one field:
 * {@link AllergySubmission.supersedes} is required on a correction and refused on a create. That
 * asymmetry is the server's (`ALLERGY_SUPERSEDES_MISSING`, `ALLERGY_SUPERSEDES_NOT_ALLOWED`) and is
 * mirrored here only by the two functions being separate.
 *
 * **Not idempotent, and not retryable on a timeout.** What makes that survivable on a correction is
 * `supersedes`: a retry after an uncertain outcome names a version that is no longer current and is
 * refused with `ALLERGY_VERSION_STALE`. A *create* has no such protection and never could — two
 * allergies for the same substance are not refused (#46 point 10), because a patient really can have
 * two documented reactions to one drug, and a record that refused the second would be wrong more
 * dangerously than one holding a duplicate.
 */

import { unauthenticated } from '../auth/sessionExpiry'

/**
 * What the form sends. Mirrors `AllergyRequest`.
 *
 * Every field on every write, the same rule as a patient correction: the body is the allergy as it
 * should now read, not a list of edits. A severity left out of a correction would be
 * indistinguishable from a severity deliberately cleared, and *this reaction was milder than we
 * first thought* is a correction somebody will want to make.
 */
export interface AllergySubmission {
  /**
   * The `version_id` this correction replaces — the one the panel was showing.
   *
   * Null on a first version, where there is nothing to supersede. Required on a correction, and
   * that is what the overview's `versionId` (#46 point 9) is carried for.
   */
  readonly supersedes: string | null

  /**
   * `active`, `resolved`, `no-known-allergies` or `entered-in-error`.
   *
   * `entered-in-error` is refused as a *first* version — writing a row in order to say it is wrong
   * documents nothing — and is the whole point of a correction.
   */
  readonly status: string

  /**
   * The allergen in the documenter's words. Record content, never translated (AGENTS.md §3 rule 8).
   *
   * Null exactly when {@link status} is `no-known-allergies`, which names no substance; required
   * otherwise. The server refuses both mistakes separately (`ALLERGY_SUBSTANCE_MISSING`,
   * `ALLERGY_SUBSTANCE_NOT_ALLOWED`) so the form can say which one was made.
   */
  readonly substance: string | null

  /** What happened, as documented. Record content. */
  readonly reaction: string | null

  /** `mild`, `moderate` or `severe`, or null — and null is not `mild`. */
  readonly severity: string | null

  /** `low`, `high` or `unable-to-assess`, or null. */
  readonly criticality: string | null

  /** `confirmed` or `unconfirmed`, or null. */
  readonly verification: string | null
}

/** What a successful allergy write returns. Mirrors `WrittenAllergy`. */
export interface WrittenAllergy {
  /** The allergy's `record_id`. New on a create, unchanged by a correction. */
  readonly allergyId: string

  /** The new version's `version_id`. What the *next* correction must supersede. */
  readonly versionId: string

  /** 1 for a first version, then 2, 3 … */
  readonly version: number

  /** The `version_id` that was superseded, or null on a first version. */
  readonly supersedes: string | null
}

/**
 * Every code an allergy write can be refused with.
 *
 * Listed rather than left as `string` so a code added on the server without wording here is a
 * typecheck failure rather than a blank message on the panel that is supposed to say
 * *penicillinallergi*. `allergyRefusalMessage` is exhaustive over this by construction.
 *
 * Three are not `ALLERGY_` codes: `PATIENT_NOT_FOUND` and `PATIENT_RETRACTED` are facts about the
 * record rather than about the form, and `ROLE_MAY_NOT_DOCUMENT` comes from `AccessGate`, shared
 * with every other write in the application.
 */
export const ALLERGY_REFUSAL_CODES = [
  'ALLERGY_BODY_MISSING',
  'ALLERGY_SUPERSEDES_MISSING',
  'ALLERGY_SUPERSEDES_NOT_ALLOWED',
  'ALLERGY_STATUS_INVALID',
  'ALLERGY_STATUS_NOT_ALLOWED',
  'ALLERGY_SUBSTANCE_MISSING',
  'ALLERGY_SUBSTANCE_NOT_ALLOWED',
  'ALLERGY_FIELD_TOO_LONG',
  'ALLERGY_SEVERITY_INVALID',
  'ALLERGY_CRITICALITY_INVALID',
  'ALLERGY_VERIFICATION_INVALID',
  'ALLERGY_NOT_FOUND',
  'ALLERGY_RETRACTED',
  'ALLERGY_VERSION_STALE',
  'ALLERGY_NO_CHANGE',
  'PATIENT_NOT_FOUND',
  'PATIENT_RETRACTED',
  'ROLE_MAY_NOT_DOCUMENT',
] as const

export type AllergyRefusalCode = (typeof ALLERGY_REFUSAL_CODES)[number]

/** Params a refusal can carry. Which are present depends on the code. */
export interface AllergyRefusalParams {
  /** `ALLERGY_FIELD_TOO_LONG`: which field, by its submission name, and the limit. */
  readonly field?: string
  readonly max?: number

  /** The four vocabulary codes: what was sent, quoted back. */
  readonly status?: string
  readonly severity?: string
  readonly criticality?: string
  readonly verification?: string

  /** `ALLERGY_NOT_FOUND`: the id that did not resolve. */
  readonly allergyId?: string

  /** `ALLERGY_VERSION_STALE`: the version that was named. */
  readonly supersedes?: string

  /**
   * `ALLERGY_VERSION_STALE`: the version that is current instead — **absent when the race was lost
   * to a concurrent insert** rather than to a version that had already moved.
   *
   * Not displayed either way. The screen's answer to staleness is to refetch the patient, which
   * reads the current version from the record rather than trusting a value that could have moved
   * again between the refusal and the render.
   */
  readonly current?: string

  /** `PATIENT_NOT_FOUND`: the id that did not resolve. */
  readonly patientId?: string

  /** `ROLE_MAY_NOT_DOCUMENT`: the role that was refused. */
  readonly roleCode?: string
}

/**
 * The server refused the write, with the code it refused by.
 *
 * Distinct from {@link AllergyUnavailableError}: a refusal is something to fix on the form, and an
 * unavailable API is something to wait out **without closing the form** and losing what was typed.
 */
export class AllergyRefusedError extends Error {
  readonly code: AllergyRefusalCode | 'UNKNOWN'
  readonly params: AllergyRefusalParams
  readonly status: number

  constructor(code: AllergyRefusalCode | 'UNKNOWN', params: AllergyRefusalParams, status: number) {
    super(`Allergy write refused: ${code}`)
    this.name = 'AllergyRefusedError'
    this.code = code
    this.params = params
    this.status = status
  }
}

/** The API could not be reached, or answered something unusable. */
export class AllergyUnavailableError extends Error {
  constructor() {
    super('Allergy write unavailable')
    this.name = 'AllergyUnavailableError'
  }
}

function isRefusalCode(value: unknown): value is AllergyRefusalCode {
  return typeof value === 'string' && (ALLERGY_REFUSAL_CODES as readonly string[]).includes(value)
}

/**
 * Turns a non-2xx response into the error to throw.
 *
 * An unreadable body still becomes an {@link AllergyRefusedError} with code `UNKNOWN` rather than an
 * unavailable error, because the server did answer: nothing was written, and telling the clinician
 * to wait would be wrong.
 */
async function refusalFrom(response: Response): Promise<AllergyRefusedError> {
  try {
    const body = (await response.json()) as {
      error?: { code?: unknown; params?: AllergyRefusalParams }
    }

    return new AllergyRefusedError(
      isRefusalCode(body.error?.code) ? body.error.code : 'UNKNOWN',
      body.error?.params ?? {},
      response.status,
    )
  } catch {
    return new AllergyRefusedError('UNKNOWN', {}, response.status)
  }
}

async function post(
  url: string,
  submission: AllergySubmission,
  signal: AbortSignal,
): Promise<WrittenAllergy> {
  let response: Response

  try {
    response = await fetch(url, {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(submission),
    })
  } catch (cause) {
    // An abort is the clinician having navigated away mid-request. Re-thrown so the caller can tell
    // it from a failure — but a write that reached the server still happened, which is why the form
    // does not abort a submit in flight.
    if (signal.aborted) {
      throw cause
    }
    throw new AllergyUnavailableError()
  }

  // Before `refusalFrom`, which reads the body, and before it for the reason as much as the order:
  // a 401 is the session having ended rather than a refusal of this allergy.
  unauthenticated(response)

  if (!response.ok) {
    throw await refusalFrom(response)
  }

  try {
    return (await response.json()) as WrittenAllergy
  } catch {
    // A 201 whose body did not parse: the row is in the record and this client cannot say which
    // version it became. Reported as unavailable so the success path is not taken — the clinician
    // reopens the patient and reads what the record now says, rather than being told the write
    // failed and documenting the same allergy twice.
    throw new AllergyUnavailableError()
  }
}

/**
 * Documents an allergy that was not in the record: one `clinical.allergy` version 1 and its audit
 * row, in one transaction on the server.
 *
 * **What this writes cannot be removed** (AGENTS.md §3). An allergy documented against the wrong
 * patient is corrected to `entered-in-error` on that patient and documented again on the right one;
 * both rows stay readable, which is the design working rather than a limitation of it.
 */
export function documentAllergy(
  patientId: string,
  submission: AllergySubmission,
  signal: AbortSignal,
): Promise<WrittenAllergy> {
  return post(`/api/patients/${encodeURIComponent(patientId)}/allergies`, submission, signal)
}

/**
 * Corrects an allergy already in the record: the next version of the same `record_id`.
 *
 * The substance, the reaction, the severity, the criticality, the verification and the status can
 * all move. What cannot is which patient it is about and when it became clinically true — those are
 * inherited from the version being corrected (#46 point 2), so a correction cannot quietly turn one
 * patient's allergy into another's.
 */
export function correctAllergy(
  patientId: string,
  allergyId: string,
  submission: AllergySubmission,
  signal: AbortSignal,
): Promise<WrittenAllergy> {
  return post(
    `/api/patients/${encodeURIComponent(patientId)}/allergies/${encodeURIComponent(allergyId)}/versions`,
    submission,
    signal,
  )
}
